import React from 'react';

/**
 * RouteErrorBoundary — catches any error thrown inside a lazy-loaded route
 * so a broken chunk or a mid-render throw NEVER blanks the whole app.
 *
 * Why this exists (Feb 2026)
 * --------------------------
 * We hit repeated production reports of "blank page after clicking a link,
 * refresh fixes it". Root cause: `<Suspense fallback=…>` in App.js had no
 * ErrorBoundary sibling, so any of these three failure modes killed the
 * React tree:
 *
 *   • A code-split chunk 404 (`Loading chunk N failed`) — happens for the
 *     first ~24h after a deploy while users on the old shell try to import
 *     a hash that no longer exists on the CDN.
 *   • A synchronous throw at module top-level in a lazy page.
 *   • A render throw before the page's own error UI mounts.
 *
 * What this does
 * --------------
 *   • Catches the error, logs it, and renders a friendly retry card.
 *   • For the specific "chunk load failed" family we auto-reload ONCE
 *     (guarded by sessionStorage) — that heals the after-deploy case with
 *     zero user friction. If reload STILL fails we show the retry card so
 *     the user isn't stuck in a loop.
 *
 * Placement: wrap Suspense in App.js — see the diff there.
 */
const CHUNK_ERR_PATTERNS = [
  /Loading chunk [\w-]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
];

const _isChunkError = (err) => {
  if (!err) return false;
  const msg = (err.message || String(err) || '') + ' ' + (err.name || '');
  return CHUNK_ERR_PATTERNS.some((rx) => rx.test(msg));
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log for observability but never re-throw — the whole point is to
    // survive.
    try {
      // eslint-disable-next-line no-console
      console.error('[RouteErrorBoundary] caught:', error, info);
    } catch (_e) { /* noop */ }

    // Chunk-load failures after a deploy: auto-heal by reloading once. The
    // sessionStorage guard prevents an infinite reload loop if the reload
    // itself still fails (e.g. genuine outage).
    if (_isChunkError(error)) {
      try {
        const key = 'lh_chunk_reload_at';
        const last = Number(window.sessionStorage.getItem(key) || 0);
        const now = Date.now();
        if (now - last > 30_000) {
          window.sessionStorage.setItem(key, String(now));
          window.location.reload();
        }
      } catch (_e) { /* noop */ }
    }
  }

  _retry = () => {
    this.setState({ error: null });
  };

  _hardReload = () => {
    try { window.sessionStorage.removeItem('lh_chunk_reload_at'); } catch (_e) { /* noop */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const chunkErr = _isChunkError(this.state.error);
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="route-error-boundary">
        <div className="max-w-md text-center">
          <div className="text-4xl mb-3">🛠️</div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">
            {chunkErr ? 'Just a sec — refreshing…' : 'Something went sideways.'}
          </h1>
          <p className="text-stone-600 mb-6">
            {chunkErr
              ? 'We shipped a small update while you were browsing. Reloading to pick up the latest version.'
              : 'This page hit an unexpected error. Reloading usually clears it — if not, head back to the home page.'}
          </p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this._hardReload}
              data-testid="route-error-reload"
              className="px-5 py-2.5 rounded-full bg-[#7042cc] hover:bg-[#5b35a8] text-white font-semibold text-sm"
            >
              Reload
            </button>
            <a
              href="/"
              data-testid="route-error-home"
              className="px-5 py-2.5 rounded-full border border-stone-300 hover:bg-stone-50 text-stone-800 font-semibold text-sm"
            >
              Home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
